import React, { useCallback, useEffect, useRef, useState } from "react";

import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import "./MapReviewQueueTool.css";
import {
  enqueueOffmapSync,
  ingestReviewItems,
  listReviewItems,
  listReviewVendors,
  setReviewDecision,
} from "../services/mapPolicyApi";
import { extractErrorMessage } from "../utils/errorUtils";
import ToolInfoModal from "../components/common/ToolInfoModal";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolJobsSection from "./shared/ToolJobsSection";
import useJobsPagination from "./shared/useJobsPagination";

const STATUS_DONE = "done";
const STATUS_FAILED = "failed";

const STATUS_FILTERS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "resolved", label: "Resolved" },
  { value: "", label: "All" },
];

const fmtMoney = (v) =>
  v === null || v === undefined || v === "" ? "—" : `$${Number(v).toFixed(2)}`;

const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : "—");

const toolInfo = {
  toolName: "MAP Review Queue",
  description:
    "Buyer review of off-MAP (full-priced) products and vendor price drops, by vendor. Off-MAP items are synced from live Shopify tags; price drops are saved from a Vendor MAP Report. Reviewing here never changes Shopify prices.",
  steps: [
    "Pick a vendor.",
    "Click Sync off-map to pull this vendor's off-MAP, full-priced products from Shopify (read-only).",
    "Optionally ingest price drops from a finished Vendor MAP Report job ID.",
    "Approve or reject each item. On approve, set the target price you want.",
  ],
  templates: [],
};

const guideTips = [
  "Off-MAP (full priced) = product is off MAP per its latest Shopify tag AND still at full price — a discounting opportunity.",
  "Price Drops = vendor lowered the MAP; the target is the New MAP price.",
  "Syncing is read-only on Shopify. Prices only change via the Price Update tool.",
  "Approving records your decision + target price; it does not apply anything yet.",
  "Items no longer off-MAP/full-priced are auto-resolved on the next sync.",
];

function MapReviewQueueTool() {
  const { addToast } = useToast();
  const [showInfoModal, setShowInfoModal] = useState(false);

  const [vendors, setVendors] = useState([]); // Vendor MAP Report vendors
  const [summary, setSummary] = useState({}); // vendorId -> counts
  const [selectedVendor, setSelectedVendor] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");

  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [ingestJobId, setIngestJobId] = useState("");
  const [ingesting, setIngesting] = useState(false);

  // Per-item local edits (target price + note) keyed by item id.
  const [drafts, setDrafts] = useState({});
  const [savingItemId, setSavingItemId] = useState(null);

  const {
    jobs: syncJobs,
    page: syncPage,
    totalPages: syncTotalPages,
    totalFinished: syncTotalFinished,
    setPage: setSyncPage,
    refetch: refetchSyncJobs,
  } = useJobsPagination({
    endpoint: "/map-policy/review-items/sync/jobs",
    cacheKey: "mapReviewQueue.syncJobs",
    pageSize: 5,
  });

  const loadVendors = useCallback(async () => {
    try {
      // The directory (report vendors ∪ legacy "OFF Map & Price Drops" sheet
      // vendors) comes back already decorated with pending counts.
      const rows = await listReviewVendors();
      setVendors(
        rows.map((r) => ({ vendorId: r.vendorId, displayName: r.vendorDisplayName }))
      );
      const map = {};
      rows.forEach((s) => {
        map[s.vendorId] = s;
      });
      setSummary(map);
      setSelectedVendor((prev) => prev || rows[0]?.vendorId || "");
    } catch (error) {
      console.error(error);
      addToast("Failed to load vendors.", "error");
    }
  }, [addToast]);

  const loadItems = useCallback(async () => {
    if (!selectedVendor) {
      setItems([]);
      return;
    }
    setLoadingItems(true);
    try {
      const data = await listReviewItems({
        vendorId: selectedVendor,
        status: statusFilter || undefined,
      });
      setItems(data.items || []);
      setDrafts({});
    } catch (error) {
      console.error(error);
      addToast("Failed to load review items.", "error");
    } finally {
      setLoadingItems(false);
    }
  }, [selectedVendor, statusFilter, addToast]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // When a sync job finishes, refresh the queue + vendor counts.
  const previousStatusRef = useRef({});
  useEffect(() => {
    const previous = previousStatusRef.current;
    let anyDone = false;
    syncJobs.forEach((job) => {
      const prev = previous[job.job_id];
      if (prev && prev !== job.status) {
        if (job.status === STATUS_DONE) {
          anyDone = true;
          addToast(`Off-map sync ${job.vendor || ""} complete.`, "success");
        } else if (job.status === STATUS_FAILED) {
          addToast(`Off-map sync failed: ${job.error || "unknown error"}`, "error");
        }
      }
    });
    previousStatusRef.current = Object.fromEntries(
      syncJobs.map((j) => [j.job_id, j.status])
    );
    if (anyDone) {
      loadVendors();
      loadItems();
    }
  }, [syncJobs, addToast, loadVendors, loadItems]);

  const handleSync = async () => {
    if (!selectedVendor) {
      addToast("Pick a vendor first.", "error");
      return;
    }
    setSyncing(true);
    try {
      await enqueueOffmapSync(selectedVendor);
      addToast("Off-map sync started (read-only). Tracking progress…", "info");
      if (syncPage !== 1) await setSyncPage(1);
      else await refetchSyncJobs();
    } catch (error) {
      console.error(error);
      addToast(await extractErrorMessage(error, "Failed to start sync."), "error");
    } finally {
      setSyncing(false);
    }
  };

  const handleIngest = async () => {
    const jobId = ingestJobId.trim();
    if (!jobId) {
      addToast("Enter a Vendor MAP Report job ID.", "error");
      return;
    }
    setIngesting(true);
    try {
      const res = await ingestReviewItems(jobId);
      addToast(
        `Ingested ${res.vendorDisplayName || res.vendor}: ${res.inserted} new, ${res.duplicates} already in queue.`,
        "success"
      );
      setIngestJobId("");
      await loadVendors();
      await loadItems();
    } catch (error) {
      console.error(error);
      addToast(await extractErrorMessage(error, "Failed to ingest report."), "error");
    } finally {
      setIngesting(false);
    }
  };

  const setDraft = (id, patch) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const handleDecision = async (item, decision) => {
    setSavingItemId(item.id);
    const draft = drafts[item.id] || {};
    const rawTarget = draft.targetPrice;
    const buyerTargetPrice =
      rawTarget === undefined || rawTarget === "" ? undefined : Number(rawTarget);
    const note = draft.note;
    try {
      await setReviewDecision(item.id, { decision, buyerTargetPrice, note });
      addToast(`Item ${decision}.`, "success");
      await loadVendors();
      await loadItems();
    } catch (error) {
      console.error(error);
      addToast(await extractErrorMessage(error, "Failed to save decision."), "error");
    } finally {
      setSavingItemId(null);
    }
  };

  const offMapItems = items.filter((i) => i.changeType === "off_map");
  const priceDropItems = items.filter((i) => i.changeType === "price_drop");
  const counts = summary[selectedVendor] || {};

  const renderItem = (item) => {
    const draft = drafts[item.id] || {};
    const isOffMap = item.changeType === "off_map";
    const pending = item.status === "pending";
    return (
      <div key={item.id} className="mrq-item">
        <div className="mrq-item-main">
          <div className="mrq-item-title">{item.productTitle || item.parentSku}</div>
          <div className="mrq-item-meta">
            <span className="mrq-sku">{item.parentSku}</span>
            {isOffMap && item.offMapTag ? (
              <span className="mrq-tag">{item.offMapTag}</span>
            ) : null}
            {isOffMap && item.hasStaleMapTags ? (
              <span className="mrq-badge mrq-badge-warn">stale tags</span>
            ) : null}
            <span className={`mrq-status mrq-status-${item.status}`}>{item.status}</span>
          </div>
        </div>

        <div className="mrq-item-prices">
          <span>Current <strong>{fmtMoney(item.currentPrice)}</strong></span>
          {isOffMap ? (
            <>
              <span>Compare-at <strong>{fmtMoney(item.compareAtPrice)}</strong></span>
              <span>Off MAP since <strong>{fmtDate(item.effectiveDate)}</strong></span>
            </>
          ) : (
            <span>New MAP <strong>{fmtMoney(item.newMapPrice)}</strong></span>
          )}
          {item.timesSeen > 1 ? (
            <span className="mrq-muted">seen {item.timesSeen}×</span>
          ) : null}
        </div>

        {pending ? (
          <div className="mrq-item-actions">
            <input
              type="number"
              step="0.01"
              min="0"
              className="tool-input mrq-price-input"
              placeholder={isOffMap ? "Target sale price" : `Target (${fmtMoney(item.newMapPrice)})`}
              value={draft.targetPrice ?? ""}
              onChange={(e) => setDraft(item.id, { targetPrice: e.target.value })}
            />
            <input
              type="text"
              className="tool-input mrq-note-input"
              placeholder="Note (optional)"
              value={draft.note ?? ""}
              onChange={(e) => setDraft(item.id, { note: e.target.value })}
            />
            <button
              type="button"
              className="tool-btn-primary mrq-btn"
              disabled={savingItemId === item.id}
              onClick={() => handleDecision(item, "approved")}
            >
              Approve
            </button>
            <button
              type="button"
              className="tool-btn-secondary mrq-btn"
              disabled={savingItemId === item.id}
              onClick={() => handleDecision(item, "rejected")}
            >
              Reject
            </button>
          </div>
        ) : (
          <div className="mrq-item-decided">
            {item.buyerTargetPrice !== null && item.buyerTargetPrice !== undefined ? (
              <span>Target {fmtMoney(item.buyerTargetPrice)}</span>
            ) : null}
            {item.decisionNote ? <span className="mrq-muted">“{item.decisionNote}”</span> : null}
            {item.status !== "resolved" ? (
              <button
                type="button"
                className="tool-btn-secondary mrq-btn"
                disabled={savingItemId === item.id}
                onClick={() => handleDecision(item, "pending")}
              >
                Reopen
              </button>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  const renderGroup = (label, groupItems, emptyText) => (
    <div className="mrq-group">
      <div className="mrq-group-head">
        <span className="mrq-group-label">{label}</span>
        <span className="mrq-group-count">{groupItems.length}</span>
      </div>
      {groupItems.length === 0 ? (
        <p className="tool-helper-text">{emptyText}</p>
      ) : (
        <div className="mrq-item-list">{groupItems.map(renderItem)}</div>
      )}
    </div>
  );

  return (
    <>
      <ToolPageLayout
        title="MAP Review Queue"
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <div className="tool-form-main">
          <section className="tool-section">
            <div className="tool-section-header">
              <h3 className="tool-section-title">Vendor</h3>
            </div>
            <div className="mrq-controls">
              <select
                className="tool-input"
                value={selectedVendor}
                onChange={(e) => setSelectedVendor(e.target.value)}
                disabled={vendors.length === 0}
              >
                {vendors.length === 0 ? (
                  <option value="">No vendors available</option>
                ) : (
                  vendors.map((v) => {
                    const c = summary[v.vendorId] || {};
                    const pend = c.pending ? ` (${c.pending} pending)` : "";
                    return (
                      <option key={v.vendorId} value={v.vendorId}>
                        {v.displayName || v.vendorId}
                        {pend}
                      </option>
                    );
                  })
                )}
              </select>
              <button
                type="button"
                className="tool-btn-primary mrq-btn"
                disabled={syncing || !selectedVendor}
                onClick={handleSync}
              >
                {syncing ? "Starting…" : "Sync off-map"}
              </button>
            </div>
            <p className="tool-helper-text">
              Sync reads this vendor's Shopify products and refreshes off-MAP, full-priced
              items. It never changes Shopify prices.
            </p>

            <div className="mrq-controls mrq-ingest">
              <input
                type="text"
                className="tool-input"
                placeholder="Vendor MAP Report job ID (to ingest price drops)"
                value={ingestJobId}
                onChange={(e) => setIngestJobId(e.target.value)}
              />
              <button
                type="button"
                className="tool-btn-secondary mrq-btn"
                disabled={ingesting || !ingestJobId.trim()}
                onClick={handleIngest}
              >
                {ingesting ? "Ingesting…" : "Ingest price drops"}
              </button>
            </div>
          </section>

          <section className="tool-section">
            <div className="tool-section-header mrq-review-head">
              <h3 className="tool-section-title">
                Review {counts.pending ? `· ${counts.pending} pending` : ""}
              </h3>
              <select
                className="tool-input mrq-status-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_FILTERS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            {loadingItems ? (
              <p className="tool-helper-text">Loading…</p>
            ) : items.length === 0 ? (
              <p className="tool-helper-text">
                No {statusFilter || ""} items for this vendor. Sync off-map or ingest a report
                to populate the queue.
              </p>
            ) : (
              <>
                {renderGroup(
                  "Off-MAP · full priced",
                  offMapItems,
                  "No off-MAP full-priced items."
                )}
                {renderGroup("Price Drops", priceDropItems, "No price drops.")}
              </>
            )}
          </section>

          <ToolJobsSection
            title="Sync Jobs"
            emptyText="No syncs yet."
            jobs={syncJobs}
            page={syncPage}
            totalPages={syncTotalPages}
            totalFinished={syncTotalFinished}
            onPageChange={setSyncPage}
            renderJob={(job) => {
              const progress = job.progress || {};
              return (
                <div key={job.job_id} className="tool-job-status">
                  <div className="tool-job-status-row">
                    <span className="tool-job-label">Vendor</span>
                    <span className="tool-job-value">{(job.vendor || "").toUpperCase()}</span>
                  </div>
                  <div className="tool-job-status-row">
                    <span className="tool-job-label">Status</span>
                    <span className={`tool-job-value tool-job-status-${job.status}`}>
                      {job.status}
                      {progress.stage ? ` (${progress.stage})` : ""}
                    </span>
                  </div>
                  {job.status === STATUS_DONE ? (
                    <div className="tool-job-counts">
                      <span>Off MAP: {progress.off_map || 0}</span>
                      <span>Full-priced saved: {progress.full_priced || 0}</span>
                      <span>Discounted skipped: {progress.discounted || 0}</span>
                      <span>Auto-resolved: {progress.resolved || 0}</span>
                    </div>
                  ) : null}
                  {job.status === STATUS_FAILED && job.error ? (
                    <div className="tool-job-error">Error: {job.error}</div>
                  ) : null}
                </div>
              );
            }}
          />
        </div>
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}

export default MapReviewQueueTool;
